import {
  allowedNextKinds,
  currentCharterParty,
  type NextOperation,
  SIDE_KINDS,
  type VoyageReportKind,
} from '@felix/domain';
import { nextNoonAt } from './noon-report.js';
import { UNLINKED } from './screen-load.js';
import type { CharterPartyItem, VoyageReport } from './types.js';
import { assignedVoyageIdOf } from './voyage-record.js';

/**
 * Sıradaki rapor (ADR 0157 §5, #2270–#2271). Plan, raporla kaydedilen Next Operation seçimidir
 * (ADR 0057 §5); burada yalnız okunur: hangi tür, ne zaman, ne kadar kaldı. "Sonraki Rapor"
 * düğmeleri hiçbir şey kaydetmez, bekleyen rapor kaydı yoktur. Saf fonksiyonlar.
 */

const byTime = (a: VoyageReport, b: VoyageReport) =>
  Date.parse(a.reportedAt) - Date.parse(b.reportedAt);

export interface NextDue {
  operation: NextOperation;
  /** Öne çıkan tür: listede Noon varsa Noon, yoksa ilk tür. */
  primary: VoyageReportKind;
  /** Öteki akış türleri ("ya da …"). */
  others: VoyageReportKind[];
  /** Noon'un zamanı (UTC); Noon yoksa `null`. */
  dueAt: string | null;
  /** Zamanın saat farkı: planı veren raporun farkı. */
  tzOffsetMin: number;
  /** Kalan dakika; eksi gecikme. Zaman yoksa `null`. */
  remainingMin: number | null;
  /** Son akış raporu taslaksa o ("Taslak bekliyor"). */
  draft: VoyageReport | null;
}

/**
 * Geminin zaman çizgisinden (ADR 0083 §1) sıradaki rapor. Plan son gönderilmiş akış raporunun
 * Next Operation'ıdır; seçimi yoksa `null`. Noon'un zamanı o raporun saat farkıyla bir sonraki
 * 12:00 (`nextNoonAt`).
 */
export function nextDue(timeline: readonly VoyageReport[], now: Date): NextDue | null {
  const list = timeline.filter((r) => r.status !== 'void').sort(byTime);
  const flow = list.filter((r) => !SIDE_KINDS.has(r.kind));
  const submitted = flow.filter((r) => r.status === 'submitted');
  const basis = submitted.at(-1);
  if (!basis) return null;
  const allowed = allowedNextKinds(list.filter((r) => r.status === 'submitted'));
  if (!allowed) return null;
  const kinds = allowed.kinds.filter((kind) => !SIDE_KINDS.has(kind));
  const primary = kinds.includes('noon') ? 'noon' : kinds[0];
  if (!primary) return null;
  const dueAt = primary === 'noon' ? nextNoonAt(basis.reportedAt, basis.tzOffsetMin) : null;
  const last = flow.at(-1);
  return {
    operation: allowed.operation,
    primary,
    others: kinds.filter((kind) => kind !== primary),
    dueAt,
    tzOffsetMin: basis.tzOffsetMin,
    remainingMin: dueAt === null ? null : Math.floor((Date.parse(dueAt) - now.getTime()) / 60_000),
    draft: last && last.status !== 'submitted' ? last : null,
  };
}

/**
 * Kiraya Giriş önerisi (ADR 0157 §7, #2273): son gönderilmiş kalkış "Kiralı", ardından On Hire
 * yok ve kalkışı kapsayan, fiili (rapora ya da seferin başlangıcına dayanan) dönemi olan CP yok.
 * Yalnız yönlendirme; CP'yi ofis açar (ADR 0084 §1), S15 ve `cpPeriod` aynen.
 */
export function onHireSuggested(
  timeline: readonly VoyageReport[],
  charterParties: readonly CharterPartyItem[],
): boolean {
  const list = timeline.filter((r) => r.status !== 'void').sort(byTime);
  const departure = list.filter((r) => r.kind === 'departure' && r.status === 'submitted').at(-1);
  if (!departure) return false;
  const classification = (departure.data.classification ?? {}) as Record<string, unknown>;
  if (classification.employment !== 'chartered') return false;
  const at = Date.parse(departure.reportedAt);
  if (list.some((r) => r.kind === 'on_hire' && Date.parse(r.reportedAt) >= at)) return false;
  const actual = charterParties.filter(
    (cp) => cp.period.startBasis !== null && cp.period.startBasis !== 'planned',
  );
  return currentCharterParty(actual, departure.reportedAt) === null;
}

/** Kalan süre metni için: saat ve dakika (mutlak). */
export function splitMinutes(total: number): { hours: number; minutes: number } {
  const abs = Math.abs(total);
  return { hours: Math.floor(abs / 60), minutes: abs % 60 };
}

/**
 * Sonraki Rapor kartının "Aç" bağlantısı (ADR 0157 §5, #2278). Bekleyen taslak kendi bağlamında
 * açılır (sefersizse `?voyage=none`): bağlamsız `?report=` taslağı planlanan sefere düşürüyordu.
 * Taslak yoksa yeni form ekranın bağlamında (`contextVoyageId`; sefersizse `null`).
 */
export function nextReportPath(
  kind: VoyageReportKind,
  draft: VoyageReport | null | undefined,
  contextVoyageId: string | null,
): string {
  const screen = kind === 'noon' ? '/voyage/noon-report' : '/voyage/legs';
  if (draft && draft.kind === kind) {
    return `${screen}?voyage=${assignedVoyageIdOf(draft) ?? UNLINKED}&report=${draft.id}`;
  }
  const voyage = contextVoyageId ?? UNLINKED;
  return kind === 'noon' ? `${screen}?voyage=${voyage}` : `${screen}?kind=${kind}&voyage=${voyage}`;
}
